import { useState, useCallback, type FC } from "react";
import { WorkflowList } from "@/components/automations/AutomationList";
import { AutomationsEducationPage } from "@/routes/automations/AutomationsEducationPage";
import { AutomationsPermissionDeniedPage } from "@/routes/automations/AutomationsPermissionDeniedPage";
import { AutomationsPageHeader } from "@/routes/automations/AutomationsPageHeader";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { useFlagWorkflowsEnabled, WorkflowAccessLevel } from "@/hooks/use-flag";
import { NewAutomationButton } from "@/components/automations/NewAutomationButton";
import { useCreateWorkflow } from "@/queries/automation-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { useNavigate } from "react-router-dom";
import { workflowDetailsUrl } from "@/utils/automations";
import type { AutomationTemplate } from "@/utils/automation-templates";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useOrganization } from "@/queries/organization-queries";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";

export const AutomationsPage: FC = () => {
    const { accessLevel: workflowAccessLevel, loading: isLoadingFlag } = useFlagWorkflowsEnabled();
    const { data: organization } = useOrganization();
    const { hasPermission, isLoading: isLoadingPermissions } = useResourcePermissions();

    const canCreateWorkflows =
        organization && hasPermission(ResourceType.ORGANIZATION, organization.id, "workflow:create");

    if (isLoadingFlag || isLoadingPermissions) {
        return <LoadingState />;
    }

    switch (workflowAccessLevel) {
        case WorkflowAccessLevel.Full:
            // Check if user has permission to create workflows
            if (!canCreateWorkflows) {
                return <AutomationsPermissionDeniedPage />;
            }
            return <AutomationsPageContent />;
        case WorkflowAccessLevel.Teaser:
            return <AutomationsEducationPage />;
        case WorkflowAccessLevel.None:
        default:
            return null;
    }
};

const AutomationsPageContent: FC = () => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const [isCreating, setIsCreating] = useState(false);
    const createWorkflowMutation = useCreateWorkflow();

    const handleTemplateSelect = useCallback(
        async (template: AutomationTemplate) => {
            setIsCreating(true);
            try {
                const workflow = await createWorkflowMutation.mutateAsync({
                    name: template.title,
                    description: template.description,
                    triggers: [template.trigger],
                    steps: template.steps,
                });
                navigate(`${workflowDetailsUrl({ id: workflow.id })}#definition`, {
                    state: { edit: true },
                });
            } catch (error) {
                toast({
                    title: "Failed to create automation",
                    description: formatError(error),
                });
            } finally {
                setIsCreating(false);
            }
        },
        [createWorkflowMutation, navigate, toast],
    );

    return (
        <div data-testid="workflows-page" className="flex flex-col gap-6">
            <AutomationsPageHeader
                actions={
                    <NewAutomationButton
                        onTemplateSelect={handleTemplateSelect}
                        disabled={isCreating}
                        testId="new-automation-dialog-workflows-page"
                        trackingId="show-new-automation-dialog-automations-page"
                    />
                }
            />
            <WorkflowList onTemplateSelect={handleTemplateSelect} />
        </div>
    );
};
